/**
 * Knowledge base dashboard: categories, tags, recent articles.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, Row, Col, Button, Empty, Spin } from 'antd';
import { FolderOutlined, TagOutlined, FileTextOutlined, PlusOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../hooks/useAuth';
import {
  LoadingContainer,
  PageTitle,
  PageSubtitle,
  FlexColumn,
  FlexWrap,
} from '../components/knowledgeBase/KnowledgeBase.styles';
import { useKBCategories, useKBTags, useKBArticles } from '../hooks/useKnowledgeBase';
import { SuperAdminRole } from '@municipality/types';

function getDisplayName(
  translations: Record<string, { name?: string }>,
  primary: string
): string {
  const p = translations[primary]?.name;
  if (p) return p;
  const en = translations.en?.name;
  if (en) return en;
  return Object.values(translations)[0]?.name ?? '';
}

function getArticleTitle(
  translations: Record<string, { title?: string }>,
  primary: string
): string {
  const p = translations[primary]?.title;
  if (p) return p;
  const en = translations.en?.title;
  if (en) return en;
  return Object.values(translations)[0]?.title ?? '';
}

const ROLES_CAN_CREATE_ARTICLE = [SuperAdminRole.SUPER_ADMIN, SuperAdminRole.CONTENT_MANAGER];

export const KnowledgeBase: React.FC = () => {
  const { t } = useTranslation(['knowledgeBase']);
  const navigate = useNavigate();
  const { user } = useAuth();
  const superAdminRole = (user as { superAdminRole?: SuperAdminRole })?.superAdminRole;
  const canCreateArticle = !!superAdminRole && ROLES_CAN_CREATE_ARTICLE.includes(superAdminRole);

  const { data: categories, isLoading: catLoading } = useKBCategories();
  const { data: tags, isLoading: tagLoading } = useKBTags();
  const { data: articles, isLoading: artLoading } = useKBArticles({ maxResults: 8 });

  const recentArticles = articles?.slice(0, 6) ?? [];

  if (catLoading || tagLoading || artLoading) {
    return (
      <LoadingContainer>
        <Spin size="large" />
      </LoadingContainer>
    );
  }

  return (
    <div>
      <PageTitle>{t('dashboard.title')}</PageTitle>
      <PageSubtitle type="secondary">
        {t('dashboard.subtitle')}
      </PageSubtitle>

      <Row gutter={[24, 24]}>
        <Col xs={24} md={8}>
          <Card
            title={
              <span>
                <FolderOutlined style={{ marginRight: 8 }} />
                {t('categories.title')}
              </span>
            }
            extra={
              <Button type="link" onClick={() => navigate('/knowledge-base/categories')}>
                {t('categories.create')}
              </Button>
            }
          >
            {!categories?.length ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('categories.noCategories')} />
            ) : (
              <FlexColumn>
                {categories.slice(0, 5).map((c) => (
                  <Button
                    key={c.id}
                    type="text"
                    block
                    style={{ textAlign: 'left' }}
                    onClick={() => navigate('/knowledge-base/categories')}
                  >
                    {getDisplayName(c.translations, c.languageSettings.primaryLanguage)}
                  </Button>
                ))}
                {categories.length > 5 && (
                  <Button type="link" onClick={() => navigate('/knowledge-base/categories')}>
                    View all ({categories.length})
                  </Button>
                )}
              </FlexColumn>
            )}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title={
              <span>
                <TagOutlined style={{ marginRight: 8 }} />
                {t('tags.title')}
              </span>
            }
            extra={
              <Button type="link" onClick={() => navigate('/knowledge-base/tags')}>
                {t('tags.create')}
              </Button>
            }
          >
            {!tags?.length ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('tags.noTags')} />
            ) : (
              <FlexWrap>
                {tags.slice(0, 10).map((tag) => (
                  <span key={tag.id} style={{ marginBottom: 4 }}>
                    {getDisplayName(tag.translations, tag.languageSettings.primaryLanguage)}
                    {tag !== tags[tags.length - 1] && ','}
                  </span>
                ))}
                {tags.length > 10 && (
                  <Button type="link" onClick={() => navigate('/knowledge-base/tags')}>
                    +{tags.length - 10} more
                  </Button>
                )}
              </FlexWrap>
            )}
          </Card>
        </Col>

        <Col xs={24} md={8}>
          <Card
            title={
              <span>
                <FileTextOutlined style={{ marginRight: 8 }} />
                {t('dashboard.recentArticles')}
              </span>
            }
            extra={
              canCreateArticle ? (
                <Button
                  type="link"
                  icon={<PlusOutlined />}
                  onClick={() => navigate('/knowledge-base/articles/create')}
                >
                  {t('articles.create')}
                </Button>
              ) : null
            }
          >
            {!recentArticles.length ? (
              <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('dashboard.noArticles')} />
            ) : (
              <FlexColumn>
                {recentArticles.map((a) => (
                  <Button
                    key={a.id}
                    type="text"
                    block
                    style={{ textAlign: 'left' }}
                    onClick={() => navigate(`/knowledge-base/articles/edit/${a.id}`)}
                  >
                    {getArticleTitle(a.translations, a.languageSettings.primaryLanguage)}
                    {a.status === 'draft' && (
                      <span style={{ marginLeft: 8, color: '#999' }}>(draft)</span>
                    )}
                  </Button>
                ))}
              </FlexColumn>
            )}
          </Card>
        </Col>
      </Row>
    </div>
  );
};
